import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, Renderer2, Input } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { SidebarService, ISidebar } from '../sidebar/sidebar.service';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, takeUntil } from 'rxjs/operators';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { AuthService, AuthState } from 'src/app/auth/auth.service/auth.service';
import { environment } from 'src/environments/environment';
import { getThemeColor, setThemeColor } from 'src/app/utils/util';
import { BACK_SETTINGS_REDIRECT, SIDEBAR_CLOSED_STATE } from '../../../shared/constants/value';
import { AppSettingsService } from 'src/app/shared/services/app-settings/app-settings.service';
import { LocalStorageService } from 'src/app/shared/services/storage/local-storage/local-storage.service';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { DestroyService } from '../../../shared/services/destroy/destroy.service';

@Component({
  selector: 'app-topnav',
  templateUrl: './topnav.component.html',
  styleUrls: ['./topnav.component.scss'],
  providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }, DestroyService],
})
export class TopnavComponent implements OnInit, OnDestroy {
  @ViewChild('toggleButton') toggleButton: ElementRef;
  @ViewChild('menu') menu: ElementRef;
  @Input() hideSettingsMenu: boolean;

  adminRoot = environment.adminRoot;
  sidebar: ISidebar;
  sidebarSubscription: Subscription;
  displayName$: Observable<string>;
  isFullScreen = false;
  isDarkModeActive = getThemeColor().indexOf('dark') > -1;
  searchKey = '';
  showSidebarMenuBurger = true;
  hideDownloadWalletBanner = false;
  isSettingsOpened = false;

  public isAuthenticated$: Observable<boolean> = this.authService.authState$.pipe(
    map((authState) => authState === AuthState.authenticated)
  );

  public userName$: Observable<string> = this.authService.userData$.pipe(map((u) => u && u.name));

  constructor(
    private sidebarService: SidebarService,
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private langService: LangService,
    private renderer: Renderer2,
    private localStorageService: LocalStorageService,
    private location: Location,
    private destroy$: DestroyService
  ) {
    langService.init();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }

          return route;
        })
      )
      .subscribe((route) => {
        this.showSidebarMenuBurger = route.snapshot.data.showProfileMenu;
        this.hideDownloadWalletBanner = route.snapshot.data.hideDownloadWalletBanner ?? false;

        this.sidebarService.showSidebar = !!this.showSidebarMenuBurger;
      });

    this.renderer.listen('window', 'click', (e: Event) => {
      if (e.target !== this.toggleButton?.nativeElement && e.target !== this.menu?.nativeElement) {
        this.isSettingsOpened = false;
      }
    });
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event): void {
    const input = document.querySelector('.mobile-view');
    if (input && input.classList) {
      input.classList.remove('mobile-view');
    }
    this.searchKey = '';
  }

  @HostListener('document:fullscreenchange', ['$event'])
  handleFullscreen(event): void {
    this.isFullScreen = !!document.fullscreenElement;
  }

  onDarkModeChange(event): void {
    let color = getThemeColor();
    if (color.indexOf('dark') > -1) {
      color = color.replace('dark', 'light');
    } else if (color.indexOf('light') > -1) {
      color = color.replace('light', 'dark');
    }
    setThemeColor(color);
    setTimeout(() => {
      window.location.reload();
    }, 200);
  }

  fullScreenClick(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  }

  async ngOnInit(): Promise<void> {
    if (await this.authService.isLoggedIn) {
      this.displayName$ = this.authService.userData$.pipe(map((u) => u && u.name));
    }
    this.sidebarSubscription = this.sidebarService.getSidebar().subscribe(
      (res) => {
        this.sidebar = res;
      },
      (err) => {
        console.error(`An error occurred: ${err.message}`);
      }
    );
  }

  ngOnDestroy(): void {
    if (this.sidebarSubscription) {
      this.sidebarSubscription.unsubscribe();
    }
  }

  menuButtonClick = (e: { stopPropagation: () => void }, menuClickCount: number, containerClassnames: string) => {
    if (e) {
      e.stopPropagation();
    }

    setTimeout(() => {
      const event = document.createEvent('HTMLEvents');
      event.initEvent('resize', false, false);
      window.dispatchEvent(event);
    }, 350);

    if (containerClassnames.includes('main-menu-hidden')) {
      containerClassnames = containerClassnames.slice(0, containerClassnames.indexOf('main-menu-hidden') - 1);
      this.saveSidebarState(false);
    } else {
      containerClassnames += ' main-menu-hidden';
      this.saveSidebarState(true);
    }

    this.sidebarService.setContainerClassnames(
      menuClickCount++ === 0 ? 2 : 4, // consider only two clicks - either show both menus (4 clicks) or hide both (2 clicks)!
      containerClassnames,
      this.sidebar.selectedMenuHasSubItems
    );
  };

  mobileMenuButtonClick = (event: { stopPropagation: () => void }, containerClassnames: string) => {
    if (event) {
      event.stopPropagation();
    }
    this.sidebarService.clickOnMobileMenu(containerClassnames);
  };

  async onSignOut(e: { stopPropagation: () => void }): Promise<void> {
    if (e) {
      e.stopPropagation();
    }

    this.authService.logoff();

    await this.router.navigate([{ outlets: { popup: null } }]);

    this.router.navigate(['/profiles/select']); // note: this is required for successful logout from subpages!
  }

  searchKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.search();
    } else if (event.key === 'Escape') {
      const input = document.querySelector('.mobile-view');
      if (input && input.classList) {
        input.classList.remove('mobile-view');
      }
      this.searchKey = '';
    }
  }

  searchAreaClick(event): void {
    event.stopPropagation();
  }
  searchClick(event): void {
    if (window.innerWidth < environment.menuHiddenBreakpoint) {
      let elem = event.target;
      if (!event.target.classList.contains('search')) {
        if (event.target.parentElement.classList.contains('search')) {
          elem = event.target.parentElement;
        } else if (event.target.parentElement.parentElement.classList.contains('search')) {
          elem = event.target.parentElement.parentElement;
        }
      }

      if (elem.classList.contains('mobile-view')) {
        this.search();
        elem.classList.remove('mobile-view');
      } else {
        elem.classList.add('mobile-view');
      }
    } else {
      this.search();
    }
    event.stopPropagation();
  }

  search(): void {
    if (this.searchKey && this.searchKey.length > 1) {
      this.router.navigate([this.adminRoot + '/pages/miscellaneous/search'], {
        queryParams: { key: this.searchKey.toLowerCase().trim() },
      });
      this.searchKey = '';
    }
  }

  onCogClick() {
    this.isSettingsOpened = !this.isSettingsOpened;
  }

  saveRedirectUrl() {
    this.localStorageService.write(BACK_SETTINGS_REDIRECT, this.location.path());
  }

  saveSidebarState(flag: boolean) {
    this.localStorageService.write(SIDEBAR_CLOSED_STATE, flag);
  }
}
